import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api, API_ROUTERS } from '../../../services/api';
import CadastrosNota from './view';
import { FormDataNota, Nota, Entregador } from './types';
import Store, { useAppSelector, useAppDispatch } from '../../../store';

const CadastrosNotaContainer: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);

  const initialFormData: FormDataNota = {
    chaveNota: '',
    entregador: '',
    empresa: user.empresa || '',
    data: new Date().toISOString().split('T')[0],
  };

  const [formData, setFormData] = useState<FormDataNota>(initialFormData);
  const [entregadores, setEntregadores] = useState<Entregador[]>([]);
  const [error, setError] = useState<string>('');
  const [notas, setNotas] = useState<Nota[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<string>(
    location.search.includes('data=')
      ? new URLSearchParams(location.search).get('data') ||
          new Date().toISOString().split('T')[0]
      : new Date().toISOString().split('T')[0]
  );
  const [notaParaCancelar, setNotaParaCancelar] = useState<Nota | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    let dataParam = params.get('data');
    if (dataParam) {
      setSelectedDate(dataParam);
    } else {
      dataParam = new Date().toISOString().split('T')[0];
      setSelectedDate(dataParam);
      navigate(`?data=${dataParam}`);
    }
    carregarEntregadores(dataParam || '');
  }, [location]);

  const carregarEntregadores = async (data: string) => {
    try {
      if (entregadores.length == 0) {
        Store.load.show(dispatch, 'Consultando entregadores');
        setEntregadores(
          api.utils.trataResponseArray(
            await api.get(API_ROUTERS.ENTREGADORES)
          ) as Entregador[]
        );
        Store.load.hide(dispatch);
      }
    } catch (err) {
      console.error('Erro ao carregar entregadores:', err);
      setError('Erro ao carregar lista de entregadores');
      Store.load.hide(dispatch);
    } finally {
      if (data != '') {
        carregarNotas(data);
      }
    }
  };

  const carregarNotas = async (data: string) => {
    try {
      Store.load.show(dispatch, 'Consultando notas');
      const notas = api.utils.trataResponseArray(
        await api.get(`${API_ROUTERS.NOTA}?data=${data}`),
        (nota) => ({
          codigo: nota.codigo,
          chaveNota: nota.chave,
          entregador: nota.usuario,
          entregadorCodigo: nota.codigousuario,
          empresa: nota.empresa,
          data: nota.data,
        })
      ) as Nota[];

      setNotas(notas);
    } catch (err) {
      console.error('Erro ao carregar notas:', err);
      setError('Erro ao carregar notas');
    } finally {
      Store.load.hide(dispatch);
    }
  };

  const handleDateChange = (data: string) => {
    setSelectedDate(data);
    navigate(`?data=${data}`);
  };

  const handleOpenModal = () => {
    setFormData({ ...initialFormData, data: selectedDate });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setFormData(initialFormData);
    setIsModalOpen(false);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (!formData.chaveNota) {
        throw new Error(
          'Por favor, informe a chave da nota ou código do pedido'
        );
      }

      if (!formData.entregador) {
        throw new Error('Por favor, selecione um entregador');
      }

      if (!formData.empresa) {
        throw new Error(
          'Empresa não identificada. Por favor, faça login novamente.'
        );
      }

      Store.load.show(dispatch, 'Cadastrando nota');
      const dataToSend = {
        empresa: formData.empresa,
        usuario: parseInt(formData.entregador),
        data: selectedDate,
        chave: formData.chaveNota,
      };

      await api.post(API_ROUTERS.NOTA, dataToSend);
      Store.dialog.alert(dispatch, 'Nota cadastrada com sucesso!', 'success');
      handleCloseModal();
      carregarNotas(selectedDate);
    } catch (err) {
      console.error('Erro ao cadastrar nota:', err);
      Store.dialog.alert(
        dispatch,
        err instanceof Error ? err.message : 'Erro ao cadastrar nota',
        'error'
      );
    } finally {
      Store.load.hide(dispatch);
    }
  };

  useEffect(() => {
    if (notaParaCancelar) {
      Store.dialog.confirm(
        dispatch,
        `Tem certeza que deseja cancelar a nota ${notaParaCancelar?.chaveNota}?`,
        confirmarCancelamento,
        () => setNotaParaCancelar(null)
      );
    }
  }, [notaParaCancelar]);

  const handleCancelarNota = async (nota: Nota) => {
    setNotaParaCancelar(nota);
  };

  const confirmarCancelamento = async () => {
    if (!notaParaCancelar) return;

    try {
      Store.load.show(dispatch, 'Apagando nota');
      await api.delete(`${API_ROUTERS.NOTA}/${notaParaCancelar.codigo}`);
      Store.dialog.alert(dispatch, 'Nota cancelada com sucesso!', 'success');
      carregarNotas(selectedDate);
    } catch (err) {
      console.error('Erro ao cancelar nota:', err);
      Store.dialog.alert(dispatch, 'Erro ao cancelar a nota', 'error');
    } finally {
      Store.load.hide(dispatch);
      setNotaParaCancelar(null);
    }
  };

  return (
    <CadastrosNota
      selectedDate={selectedDate}
      error={error}
      notas={notas}
      formData={formData}
      entregadores={entregadores}
      isModalOpen={isModalOpen}
      handleDateChange={handleDateChange}
      handleOpenModal={handleOpenModal}
      handleCloseModal={handleCloseModal}
      handleSubmit={handleSubmit}
      handleChange={handleChange}
      handleCancelarNota={handleCancelarNota}
    />
  );
};

export default CadastrosNotaContainer;
